import { useNavigate } from 'react-router-dom';
import { useIsAuthenticated } from '@azure/msal-react';
import { useEffect, useState } from 'react';
import {
  ChatBubbleLeftRightIcon,
  CameraIcon,
  ShieldCheckIcon,
  SparklesIcon,
  ArrowRightIcon,
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const SITE_BASE = 'https://www.simplytechwise.com';

const navigation = [
  { name: 'Home', href: SITE_BASE },
  { name: 'About', href: `${SITE_BASE}/about-us` },
  { name: 'Services', href: `${SITE_BASE}/services` },
  { name: 'Resources', href: `${SITE_BASE}/resources` },
  { name: 'Contact', href: `${SITE_BASE}/contact` },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // If already authenticated, redirect to chat
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/chat', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const benefits = [
    {
      icon: ChatBubbleLeftRightIcon,
      title: 'Plain-Language Answers',
      description: 'Get tech help explained simply—no jargon, no judgment. We meet you at your level.',
    },
    {
      icon: CameraIcon,
      title: 'Share Photos for Help',
      description: 'Snap a picture of an error message or confusing screen, and we\'ll walk you through it.',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Stay Safe Online',
      description: 'Learn to spot scams, protect your accounts, and browse with confidence.',
    },
  ];

  const howItWorks = [
    {
      step: '1',
      title: 'Sign In',
      description: 'Create your free account or sign in with your existing email.',
    },
    {
      step: '2',
      title: 'Ask Your Question',
      description: 'Type your tech question or upload a photo of what\'s confusing you.',
    },
    {
      step: '3',
      title: 'Get Personalized Help',
      description: 'Receive step-by-step guidance tailored to your situation.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-wise-light to-white">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <a href={SITE_BASE} className="flex items-center gap-2">
              <img src="/simplytechwise-logo.png" alt="Simply TechWise" className="h-10" />
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navigation.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="px-3 py-2 text-gray-600 hover:text-wise-dark font-medium rounded-lg hover:bg-gray-100 transition-colors"
                >
                  {item.name}
                </a>
              ))}
              <span className="px-3 py-2 text-wise font-semibold">
                WiseAssist
              </span>
              <button
                onClick={() => navigate('/join')}
                className="ml-2 px-4 py-2 bg-wise text-white font-medium rounded-lg hover:bg-wise-dark transition-colors"
              >
                Sign In
              </button>
            </nav>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-navigation"
            >
              {mobileMenuOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div
          id="mobile-navigation"
          className={`md:hidden bg-white border-t border-gray-100 ${
            mobileMenuOpen ? '' : 'hidden'
          }`}
        >
          <div className="px-4 py-3 space-y-1">
            {navigation.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="block px-3 py-2 text-gray-600 hover:text-wise-dark font-medium rounded-lg hover:bg-gray-100 transition-colors"
              >
                {item.name}
              </a>
            ))}
            <div className="px-3 py-2 text-wise font-semibold border-t border-gray-100 mt-2 pt-2">
              WiseAssist <span className="text-xs text-gray-500 font-normal">(You are here)</span>
            </div>
            <button
              onClick={() => navigate('/join')}
              className="w-full mt-2 px-4 py-3 bg-wise text-white font-medium rounded-lg hover:bg-wise-dark transition-colors"
            >
              Sign In
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 sm:py-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-wise/10 rounded-full text-wise-dark font-medium text-sm mb-6">
            <SparklesIcon className="h-4 w-4" />
            AI-Powered Tech Support
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Your Friendly<br />
            <span className="text-wise">Tech Support Assistant</span>
          </h1>
          
          <p className="text-xl sm:text-2xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
            Technology doesn't have to be confusing. Get personalized help with everyday tech questions—explained simply, step by step.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/join')}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-wise text-white font-semibold rounded-xl hover:bg-wise-dark transition-colors text-lg shadow-lg shadow-wise/25"
            >
              Get Started Free
              <ArrowRightIcon className="h-5 w-5" />
            </button>
            <a
              href="https://www.simplytechwise.com/services"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-gray-700 font-semibold rounded-xl border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors text-lg"
            >
              Learn About Our Services
            </a>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 sm:py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Tech Help That Actually Helps
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Whether you're a senior staying connected, a parent protecting your family online, or just someone who needs tech explained simply—we're here for you.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow"
              >
                <div className="w-14 h-14 bg-wise/10 rounded-xl flex items-center justify-center mb-6">
                  <benefit.icon className="h-7 w-7 text-wise" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 sm:py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-gray-600">
              Getting help is easy—just three simple steps.
            </p>
          </div>

          <div className="space-y-8">
            {howItWorks.map((item, index) => (
              <div
                key={index}
                className="flex gap-6 items-start"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-wise text-white rounded-full flex items-center justify-center font-bold text-xl">
                  {item.step}
                </div>
                <div className="pt-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-lg">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button
              onClick={() => navigate('/join')}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-wise text-white font-semibold rounded-xl hover:bg-wise-dark transition-colors text-lg shadow-lg shadow-wise/25"
            >
              Start Getting Help Now
              <ArrowRightIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 sm:py-20 px-4 bg-wise-dark text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Brought to You by Simply TechWise
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
            We're an Oklahoma-based, woman-owned tech company focused on helping everyday people turn their tech worries into tech wins.
          </p>
          <div className="flex flex-wrap justify-center gap-8 text-white/70">
            <div className="flex items-center gap-2">
              <ShieldCheckIcon className="h-5 w-5" />
              <span>Private & Secure</span>
            </div>
            <div className="flex items-center gap-2">
              <ChatBubbleLeftRightIcon className="h-5 w-5" />
              <span>No Jargon</span>
            </div>
            <div className="flex items-center gap-2">
              <SparklesIcon className="h-5 w-5" />
              <span>AI-Powered</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-white border-t border-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Logo & Description */}
            <div className="md:col-span-2">
              <a href={SITE_BASE}>
                <img src="/simplytechwise-logo.png" alt="Simply TechWise" className="h-10 mb-4" />
              </a>
              <p className="text-gray-600 max-w-md">
                Helping everyday people turn their tech worries into tech wins. Oklahoma-based, woman-owned.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Quick Links</h4>
              <ul className="space-y-2">
                {navigation.map((item) => (
                  <li key={item.name}>
                    <a
                      href={item.href}
                      className="text-gray-600 hover:text-wise transition-colors"
                    >
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Get in Touch</h4>
              <ul className="space-y-2 text-gray-600">
                <li>
                  <a
                    href="mailto:support@simplytechwise.com"
                    className="hover:text-wise transition-colors"
                  >
                    support@simplytechwise.com
                  </a>
                </li>
                <li>
                  <a
                    href={`${SITE_BASE}/contact`}
                    className="hover:text-wise transition-colors"
                  >
                    Contact Form
                  </a>
                </li>
              </ul>
              {/* Social Links */}
              <div className="flex gap-4 mt-4">
                <a
                  href="https://www.facebook.com/simplytechwise"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-wise transition-colors"
                  aria-label="Facebook"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a
                  href="https://www.instagram.com/simplytechwise"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-wise transition-colors"
                  aria-label="Instagram"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"/>
                  </svg>
                </a>
                <a
                  href="https://www.linkedin.com/company/simplytechwise"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-wise transition-colors"
                  aria-label="LinkedIn"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-400">
              © {new Date().getFullYear()} Simply TechWise. All rights reserved.
            </p>
            <p className="text-sm text-gray-400">
              WiseAssist - AI-powered tech support for everyday people
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
